/**
 * EmailDragManager — HTML5 Drag-and-Drop for email rows → sidebar folder zones.
 *
 * Replaces email_drag_controller.js.
 * Attaches drag events to the root layout element using event delegation,
 * so it works for all current and future rows / drop zones without needing
 * Stimulus target tracking.
 *
 * Drop zones must have:
 *   data-email-drop-zone           (presence = is a drop zone)
 *   data-drop-folder-id            (folder id, empty = inbox)
 *   data-drop-folder-key           ("inbox" = always move to inbox)
 *   data-drop-mailbox-id           (custom folder mailbox; used for isolation)
 *
 * Draggable rows must have:
 *   data-draggable-email-id        (email id)
 *   draggable="true"
 */


export class EmailDragManager {
  constructor({ element, store }) {
    this._el    = element
    this._store = store

    this._onDragStart = this._handleDragStart.bind(this)
    this._onDragEnd   = this._handleDragEnd.bind(this)
    this._onDragOver  = this._handleDragOver.bind(this)
    this._onDragEnter = this._handleDragEnter.bind(this)
    this._onDragLeave = this._handleDragLeave.bind(this)
    this._onDrop      = this._handleDrop.bind(this)
  }

  connect() {
    this._el.addEventListener('dragstart',  this._onDragStart)
    this._el.addEventListener('dragend',    this._onDragEnd)
    this._el.addEventListener('dragover',   this._onDragOver)
    this._el.addEventListener('dragenter',  this._onDragEnter)
    this._el.addEventListener('dragleave',  this._onDragLeave)
    this._el.addEventListener('drop',       this._onDrop)
  }

  disconnect() {
    this._el.removeEventListener('dragstart',  this._onDragStart)
    this._el.removeEventListener('dragend',    this._onDragEnd)
    this._el.removeEventListener('dragover',   this._onDragOver)
    this._el.removeEventListener('dragenter',  this._onDragEnter)
    this._el.removeEventListener('dragleave',  this._onDragLeave)
    this._el.removeEventListener('drop',       this._onDrop)
  }

  _emailMailboxId(emailId) {
    const row = (this._store.emails.value || []).find(e => Number(e.id) === Number(emailId))
    return row?.mailbox_id ?? null
  }

  _folderCompatibleWithEmail(zone, emailId) {
    const folderMailboxId = zone.dataset.dropMailboxId
    if (!folderMailboxId) return true // system folders / inbox
    const emailMailboxId = this._emailMailboxId(emailId)
    if (emailMailboxId == null) return true
    return Number(folderMailboxId) === Number(emailMailboxId)
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  _handleDragStart(e) {
    const row = e.target.closest('[data-draggable-email-id]')
    if (!row) return
    const emailId = row.dataset.draggableEmailId
    if (!emailId) return

    e.dataTransfer.setData('text/plain', emailId)
    e.dataTransfer.effectAllowed = 'move'
    row.classList.add('email-dragging')
  }

  _handleDragEnd(e) {
    const row = e.target.closest('[data-draggable-email-id]')
    if (row) row.classList.remove('email-dragging')

    this._el.querySelectorAll('[data-email-drop-zone]').forEach(el => {
      el.classList.remove('email-drop-target', 'email-drop-target--invalid')
    })
  }

  _handleDragOver(e) {
    const zone = e.target.closest('[data-email-drop-zone]')
    if (!zone) return
    e.preventDefault()
    const emailId = e.dataTransfer.getData('text/plain')
      || this._el.querySelector('.email-dragging')?.dataset?.draggableEmailId
    if (emailId && zone.dataset.dropFolderId && !this._folderCompatibleWithEmail(zone, emailId)) {
      e.dataTransfer.dropEffect = 'none'
      return
    }
    e.dataTransfer.dropEffect = 'move'
  }

  _handleDragEnter(e) {
    const zone = e.target.closest('[data-email-drop-zone]')
    if (!zone) return
    e.preventDefault()
    const emailId = this._el.querySelector('.email-dragging')?.dataset?.draggableEmailId
    if (emailId && zone.dataset.dropFolderId && !this._folderCompatibleWithEmail(zone, emailId)) {
      zone.classList.add('email-drop-target--invalid')
      return
    }
    zone.classList.add('email-drop-target')
  }

  _handleDragLeave(e) {
    const zone = e.target.closest('[data-email-drop-zone]')
    if (!zone) return
    // Only remove highlight if leaving the zone itself (not entering a child)
    if (!zone.contains(e.relatedTarget)) {
      zone.classList.remove('email-drop-target', 'email-drop-target--invalid')
    }
  }

  async _handleDrop(e) {
    const zone = e.target.closest('[data-email-drop-zone]')
    if (!zone) return
    e.preventDefault()
    zone.classList.remove('email-drop-target', 'email-drop-target--invalid')

    // Prefer dataTransfer; fall back to the dragging row (some browsers clear getData).
    const emailId = e.dataTransfer.getData('text/plain')
      || this._el.querySelector('.email-dragging')?.dataset?.draggableEmailId
    if (!emailId) return

    const folderId  = zone.dataset.dropFolderId || null
    const folderKey = zone.dataset.dropFolderKey
    const id = Number(emailId)

    // System folders need state actions (restore/archive/trash), not just folder_id moves.
    // Inbox / empty folder id: single restoreToInbox (one count transition + one refresh).
    if (folderKey === 'inbox' || (!folderKey && !folderId)) {
      await this._store.restoreToInbox(id)
      return
    }
    if (folderKey === 'archived') {
      await this._store.updateEmailState(id, 'archive')
      return
    }
    if (folderKey === 'trash') {
      await this._store.updateEmailState(id, 'trash')
      return
    }
    if (folderKey === 'starred') {
      await this._store.updateEmailState(id, 'star')
      return
    }

    // Custom folder drop — refuse cross-mailbox moves (server would 422).
    if (folderId) {
      if (!this._folderCompatibleWithEmail(zone, id)) {
        console.warn('[EmailDragManager] Folder belongs to a different mailbox; move skipped')
        return
      }
      await this._store.moveToFolder(id, Number(folderId))
    }
  }
}
